import { CounterpartyIdAndAlias, CounterpartyInstruction, EntityType, SettlementVenue, ValidationIssue } from 'api'
import { groupBy, uniq } from 'lodash'
import React, { useRef } from 'react'
import { useSelector } from 'react-redux'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import {
  CounterpartyFilter,
  CounterpartyFilterSelectorProps,
  createCounterpartyNamesAndAliasesListDetailsSelector
} from 'selectors/tradingEntitySelectors'
import { createUserCounterpartyOptionGroupsSelector } from 'selectors/userPreferenceSelectors'
import { notNilFilter, Nullable, useParametricSelector } from 'utils'
import { GroupedMultiCombobox, GroupedMultiComboboxProps } from 'view/components/options'

export interface AliasesMultiComboboxProps
  extends Omit<GroupedMultiComboboxProps<string, string>, 'optionKeys' | 'onUpdate' | 'value' | 'optionGroups'> {
  filters: CounterpartyFilter[]
  clearingHouseId: string | null
  value: CounterpartyInstruction[]
  settlementVenue: Nullable<SettlementVenue>
  onUpdate: (counterpartyIdsAndAliases: CounterpartyIdAndAlias[]) => void
  validationIssues?: ValidationIssue[] | undefined
}

const filtersSelector = (_: {}, props: CounterpartyFilterSelectorProps): CounterpartyFilter[] => props.filters
const counterpartyMultiClearingHouseIdSelector = (_: {}, props: CounterpartyFilterSelectorProps) => props.clearingHouseId
const counterpartyMultiSettlementVenueSelector = (_: {}, props: CounterpartyFilterSelectorProps) => props.settlementVenue
const organisationIdSelector = () => null
const showAliasesSelector = () => true

const aliasesSelector = createCounterpartyNamesAndAliasesListDetailsSelector(
  filtersSelector,
  counterpartyMultiSettlementVenueSelector,
  counterpartyMultiClearingHouseIdSelector,
  organisationIdSelector,
  showAliasesSelector
)

const optionsSelector = createUserCounterpartyOptionGroupsSelector()
const getTradingEntitySelector = createEntityGetterSelector(EntityType.TRADING_ENTITY)

const encodeOptionKey = (counterpartyId: string, publishedName: string, alias?: string | null) => {
  return [counterpartyId, publishedName, alias].filter(notNilFilter).join(',')
}

const decodeOptionKey = (optionKey: string): { counterpartyId: string; publishedName: string; aliasName: string | undefined } => {
  const [counterpartyId, publishedName, alias] = optionKey.split(',')

  const maybeAlias: string | undefined = alias

  return { counterpartyId, publishedName, aliasName: maybeAlias }
}

export const AliasesMultiCombobox = ({
  onUpdate,
  value,
  filters,
  clearingHouseId,
  settlementVenue,
  ...comboboxProps
}: AliasesMultiComboboxProps) => {
  const aliases = useParametricSelector(aliasesSelector, {
    filters,
    clearingHouseId,
    settlementVenue,
    organisationId: null
  })
  const getTradingEntity = useSelector(getTradingEntitySelector)
  const optionGroups = useSelector(optionsSelector)

  const prevValues = useRef<CounterpartyIdAndAlias[]>(
    value.map(v => ({ counterpartyAlias: v.counterpartyAlias, counterpartyId: v.counterpartyId }))
  )

  const optionKeys = aliases.map(a => {
    return encodeOptionKey(a.counterpartyId, a.publishedName, a.alias)
  })

  const getOption = (optionKey: string) => {
    const { publishedName, aliasName } = decodeOptionKey(optionKey)

    return aliasName || publishedName
  }

  const groups = optionGroups.map(group => ({
    ...group,
    members: group.members.map(function mapGroupMembersToOptionKeys(id: string) {
      const aliasDetail = aliases.find(a => a.counterpartyId === id)
      const aliasTypeAndValues = aliasDetail?.tradingEntityMeta?.aliasTypeAndValues

      const maybeAliasName = aliasTypeAndValues?.length === 1 ? aliasTypeAndValues[0].value : undefined

      return encodeOptionKey(id, getTradingEntity(id)?.publishedName!, maybeAliasName)
    })
  }))

  const onUpdateCombobox = (optionKeys: string[]) => {
    const counterpartyAliases = optionKeys.map<CounterpartyIdAndAlias>(option => {
      const { counterpartyId, aliasName } = decodeOptionKey(option)

      return { counterpartyId, counterpartyAlias: aliasName || null }
    })

    const byCounterpartyIds = groupBy(counterpartyAliases, ({ counterpartyId }) => counterpartyId)

    const counterpartyAliasesDismissingPrevSelectedAliasForSameCounterparty = Object.values(byCounterpartyIds).flatMap(
      counterpartyInstructions => {
        if (counterpartyInstructions.length > 1) {
          return counterpartyInstructions.filter(
            instruction => !prevValues?.current?.some(a => a.counterpartyAlias === instruction.counterpartyAlias)
          )
        }

        return counterpartyInstructions
      }
    )

    onUpdate(counterpartyAliasesDismissingPrevSelectedAliasForSameCounterparty)

    prevValues.current = counterpartyAliasesDismissingPrevSelectedAliasForSameCounterparty
  }

  const valueAsOptionKeys = uniq(
    value
      .map(v => {
        const counterparty = getTradingEntity(v.counterpartyId)

        return counterparty ? encodeOptionKey(v.counterpartyId, counterparty.publishedName, v.counterpartyAlias) : undefined
      })
      .filter(notNilFilter)
  )

  return (
    <GroupedMultiCombobox
      {...comboboxProps}
      getOptionText={getOption}
      optionKeys={optionKeys}
      optionGroups={groups}
      value={valueAsOptionKeys}
      onUpdate={onUpdateCombobox}
    />
  )
}
